/**
 * Vista previa de los libros de inventario de un ajuste (pedido del usuario del 2026-10-30), antes de «Registrar ajuste»
 * y antes de «Aprobar y aplicar». Cada renglón con la columna y el concepto del libro, si entra también al fiscal (solo
 * el inventario inicial) y el valor del inventario inicial. Los datos los da el servidor
 * (`vistaLibrosAjuste`, `apps/api/src/modulos/inventario/casos.ts`); que el libro diga lo mismo lo prueba
 * `apps/api/test/bugs/vista-libros-ajuste.spec.ts`.
 */
import { formatearCantidad, formatearTasa, Money, type VistaLibrosAjusteDTO } from '@mg/shared';
import { AvisoLibro, BsLibro, mesDelPeriodo, TarjetaVistaLibros, TituloLibro } from '@/componentes/VistaPreviaLibros.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';

const COLUMNA: Record<VistaLibrosAjusteDTO['lineas'][number]['columna'], string> = { entradas: 'Entradas', salidas: 'Salidas', retiros: 'Retiros', autoconsumos: 'Autoconsumos' };

export function VistaAjuste({ vista, al, testid = 'ajuste-vista-libros' }: { vista: VistaLibrosAjusteDTO; al: 'registrar' | 'aprobar'; testid?: string }) {
  const sinCosto = vista.lineas.filter((l) => l.valor_bs !== null && !Money.parse(l.costo_usd).esPositivo());
  const hayInicial = vista.lineas.some((l) => l.valor_bs !== null);
  return (
    <TarjetaVistaLibros testid={testid}>
      <div className="space-y-1">
        <TituloLibro
          titulo="Libro de inventario"
          testid={`${testid}-mes`}
          nota={vista.aplica_ahora && vista.mes ? `Se aplica al ${al === 'registrar' ? 'registrarlo' : 'aprobarlo'} y entra en el libro de ${mesDelPeriodo(vista.mes)}.` : 'Queda pendiente de aprobación: entra en el libro del mes en que se apruebe.'}
        />
        <ul className="space-y-1">
          {vista.lineas.map((l) => (
            <li key={l.producto_id} className="flex justify-between gap-3 border-t pt-1 first:border-t-0 first:pt-0" data-testid={`${testid}-${l.codigo}`}>
              <span className="min-w-0 break-words">
                {l.codigo} · {l.nombre}
                <span className="block text-[11px] text-muted-foreground">
                  {COLUMNA[l.columna]} · {l.concepto} <Badge variant={l.fiscal ? 'info' : 'neutro'} className="ml-1">{l.fiscal ? 'real y fiscal' : 'solo real'}</Badge>
                </span>
              </span>
              <span className="num shrink-0 text-right">
                {Money.parse(l.cantidad).esPositivo() ? '+' : ''}{formatearCantidad(Money.parse(l.cantidad))} {l.unidad}
                <span className="block text-[11px] text-muted-foreground">{l.valor_bs !== null ? <BsLibro valor={l.valor_bs} /> : 'al costo promedio del mes'}</span>
              </span>
            </li>
          ))}
        </ul>
        {hayInicial ? (
          <p className="text-[11px] text-muted-foreground">
            El inventario inicial entra con su valor: unidades × costo en $ × BCV ({formatearTasa(vista.tasa_bcv)}{vista.aplica_ahora ? '' : ', la de hoy: al aprobarse usa la de ese día'}). Lo demás va al costo promedio del mes.
          </p>
        ) : null}
        {vista.lineas.some((l) => !l.fiscal) ? <p className="text-[11px] text-muted-foreground">Al libro de inventario fiscal solo entra el «Inventario inicial».</p> : null}
        {sinCosto.length > 0 ? (
          <AvisoLibro testid={`${testid}-sin-costo`}>
            {sinCosto.map((l) => l.codigo).join(', ')} no {sinCosto.length === 1 ? 'tiene' : 'tienen'} costo (nunca se ha comprado): entra a los dos libros de inventario con valor Bs 0,00.
          </AvisoLibro>
        ) : null}
      </div>
    </TarjetaVistaLibros>
  );
}
